import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { A11yElementId } from '../../../app/package/base/service/a11y/a11y-service';
import styles from './ui-nav-bar-icon.css?inline';
import '../ui-sprite-svg/ui-sprite-svg';

export const NavBarIconLayout = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
} as const;

export type NavBarIconLayout = (typeof NavBarIconLayout)[keyof typeof NavBarIconLayout];

export const NavBarIconName = {
  ACCOUNT: 'account',
  DEPOSIT: 'deposit',
  WALLET: 'wallet',
  DISCOVER: 'discover',
  HOME: 'home',
  MISSIONS: 'missions',
  NOTIFICATIONS: 'notifications',
  PROMO: 'promo',
  SEARCH: 'search',
  SPORTS: 'sports',
  GAMES: 'games',
} as const;

export type NavBarIconName = (typeof NavBarIconName)[keyof typeof NavBarIconName];

interface NavBarIcons {
  [key: string]: string;
}

const navBarIcons: NavBarIcons = {
  account: 'nav-bar-account-icon',
  accountActive: 'nav-bar-account-icon-active',
  deposit: 'nav-bar-deposit-icon',
  depositActive: 'nav-bar-deposit-icon-active',
  discover: 'nav-bar-discover-icon',
  home: 'nav-bar-home-icon',
  homeActive: 'nav-bar-home-icon-active',
  missions: 'nav-bar-missions-icon',
  notifications: 'nav-bar-notifications-icon',
  promo: 'nav-bar-promo-icon',
  promoActive: 'nav-bar-promo-icon-active',
  search: 'nav-bar-search-icon',
  searchActive: 'nav-bar-search-icon-active',
  sports: 'nav-bar-sports-icon',
  games: 'nav-bar-games-icon',
  wallet: 'nav-bar-wallet-icon',
};

const CName = 'ui-nav-bar-icon';

@customElement(CName)
export class UiNavBarIcon extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Boolean }) active = false;
  @property({ type: String }) label = '';
  @property({ type: String }) href = '';
  @property({ type: String }) class = '';
  @property({ type: String }) layout: NavBarIconLayout = NavBarIconLayout.VERTICAL;
  @property({ type: String }) icon: NavBarIconName = NavBarIconName.DEPOSIT;
  @property({ type: Boolean }) showDot = false;
  @property({ type: Boolean }) isA11yMainNavHome = false;

  @query('#a11y-main-navigation') private _navElement?: HTMLElement;

  private readonly _hasActiveState: NavBarIconName[] = [
    NavBarIconName.ACCOUNT,
    NavBarIconName.DEPOSIT,
    NavBarIconName.HOME,
    NavBarIconName.PROMO,
    NavBarIconName.SEARCH,
  ];

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._navElement && window.$app.a11y.hasElement(A11yElementId.MAIN_NAVIGATION)) {
      window.$app.a11y.unregisterElement(A11yElementId.MAIN_NAVIGATION);
    }
  }

  firstUpdated() {
    if (this._navElement && !window.$app.a11y.hasElement(A11yElementId.MAIN_NAVIGATION)) {
      window.$app.a11y.registerElement(A11yElementId.MAIN_NAVIGATION, this._navElement);
    }
  }

  render() {
    const iconId = navBarIcons[this.icon]!;

    let activeIconId = iconId;
    if (this._hasActiveState.includes(this.icon)) {
      const activeIcon = `${this.icon}Active`;
      activeIconId = navBarIcons[activeIcon]!;
    }
    if (this.active && !this._hasActiveState.includes(this.icon)) {
      window.$app.logger.log(`No active state for ${this.icon} available`);
    }

    return html`
      <a
        class="container ${this.layout}"
        href=${this.href}
        role="button"
        @click=${(ev: PointerEvent) => {
          ev.preventDefault();
        }}
        id=${this.isA11yMainNavHome ? 'a11y-main-navigation' : ''}
      >
        <div class="icon-wrapper">
          <ui-sprite-svg class="icon ${this.active ? 'hidden' : ''}" .iconID=${iconId}></ui-sprite-svg>
          <ui-sprite-svg class="icon ${!this.active ? 'hidden' : ''}" .iconID=${activeIconId}></ui-sprite-svg>
          ${this.showDot ? html`<div class="dot"></div>` : nothing}
        </div>
        <div class="label ${this.class}">${this.label}</div>
      </a>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiNavBarIcon;
  }
}
